import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { closeSubscription, runTeardown } from '../services/safe-teardown';
import { ChartTickService } from '../services/chart-tick.service';
import { createRangeSharer } from './share-time-range';
import {
  isNoReading,
  metresToDisplay,
  displayToMetres,
} from '../services/telemetry-conversion';
import { medianGapMs } from '../services/median-gap';
import { ProjectService } from '../services/project.service';
import {
  loadSciChart,
  BoxAnnotation,
  CursorModifier,
  EAutoRange,
  EAxisAlignment,
  ELabelPlacement,
  ELegendOrientation,
  ENumericFormat,
  FastLineRenderableSeries,
  HeatmapColorMap,
  HeatmapLegend,
  LegendModifier,
  NumberRange,
  NumericAxis,
  RolloverModifier,
  RubberBandXyZoomModifier,
  SciChartSurface,
  TSciChart,
  UniformHeatmapDataSeries,
  UniformHeatmapRenderableSeries,
  VerticalLineAnnotation,
  XyDataSeries,
  ZoomPanModifier,
  formatUnixDateToHumanStringHHMMSS,
  zeroArray2D,
} from '../charts/scichart';
import { TemperatureDataRow } from '../models/temperature-data-row';
import { ZoneDataRow } from '../models/zone-data-row';
import { environment } from 'src/environments/environment';
import { Subscription } from 'rxjs';
import { FrameCoalescer } from '../charts/frame-coalescer';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { MarkerInfo } from '../models/marker-info';
import { ChartCaptureService } from '../services/chart-capture.service';
import { AdaptiveResolutionService, ringBufferHandle } from '../services/adaptive-resolution.service';
import {
  ReadonlyTelemetryBuffer,
  TelemetryChange,
  TelemetryStoreService,
} from '../services/telemetry-store.service';
import {
  rowOnLinerGrid,
  valueAtSampleIndex,
} from '../services/telemetry-downsample';
import {
  TelemetryRingBuffer,
  DEFAULT_TICK_RETENTION,
} from '../services/telemetry-ring-buffer';
import { LossDataRow } from '../models/loss-data-row';
import { ProjectLossData } from '../models/project-loss-data';
import {
  FaultSpan,
  faultSeverityColor,
} from '../health/fault-health.util';
import { FaultSpanAccumulator } from '../services/fault-span-accumulator';
import { MAX_AUTO_TICKS } from '../services/axis-scaling';
import {
  GRIDLINE_STYLE,
  WATERFALL_TEMP_DOMAIN_C,
  WATERFALL_TEMP_GRADIENT_STOPS,
  markerStroke,
} from '../services/chart-theme';
import { markerColor } from '../services/marker-color';
import { ZoomLatch } from '../services/zoom-latch';

/**
 * THE LEGEND'S TEMPERATURE DOMAIN, emitted as BOTH fragments that need it.
 *
 * The colour map paints the strip; the axis labels it. They must describe the same
 * range, and the only way to guarantee that is to compute it ONCE and hand out both
 * shapes. Returning two fragments rather than a single {min,max} is deliberate: it
 * puts the invariant somewhere a test can hold it, which a bare pair of numbers
 * cannot.
 *
 * LIMIT OF THIS GUARD, stated so nobody over-trusts it: the spec proves the two
 * fragments agree. It cannot prove createTempLegend actually USES both — a future
 * edit that hand-rolls one side bypasses this entirely. The comment at the call site
 * is the only thing guarding that, and comments do not fail builds.
 */
export function tempLegendRanges(unitTempVal: (celsius: number) => number): {
  colorMapRange: { minimum: number; maximum: number };
  axisRange: { min: number; max: number };
} {
  const min = unitTempVal(WATERFALL_TEMP_DOMAIN_C.min);
  const max = unitTempVal(WATERFALL_TEMP_DOMAIN_C.max);
  return { colorMapRange: { minimum: min, maximum: max }, axisRange: { min, max } };
}

@Component({
  selector: 'app-graph',
  templateUrl: './graph.component.html',
  styleUrls: ['./graph.component.scss'],
})
export class GraphComponent implements OnInit, OnDestroy {
  /**
   * CHART CONTAINER IDS ARE PER-INSTANCE, NOT HARDCODED.
   *
   * SciChartSurface.create() resolves its host with document.getElementById,
   * which returns the FIRST match in document order. The export prerenders a
   * SECOND copy of this component offscreen; while both copies rendered the same
   * literal id, that lookup handed the offscreen surface the MOUNTED div and
   * creating it tore down the live surface already attached there.
   *
   * The capture NAME is unchanged and is still what ChartCaptureService keys on;
   * only the DOM id is made unique.
   */
  private static nextInstanceId = 0;
  private readonly instanceSuffix = `-${++GraphComponent.nextInstanceId}`;
  readonly tempOverTimeGraphId = `temp-over-time-graph${this.instanceSuffix}`;
  readonly waterfallGraphId = `waterfall-graph${this.instanceSuffix}`;



  /**
   * True once the SciChart chunk has loaded AND the surfaces exist.
   *
   * The chart container itself is deliberately NOT *ngIf'd on this:
   * SciChartSurface.create() looks the div up by id, so it has to be in the DOM
   * before creation, not after. The flag gates a loading overlay instead, which
   * is what a user should see while a 233 kB chunk is on the wire.
   */
  chartsReady = false;

  @Input() isSubView: boolean = false;
  errorMessage = '';
  tempDataLoaded: boolean = false;
  zoneDataLoaded: boolean = false;
  /** True when the waterfall currently shows a stand-in row rather than data. */
  private waterfallIsPlaceholder = false;

  // Temp Chart
  /** Bounded row storage — see TelemetryRingBuffer. Indexed by ABSOLUTE tick. */
  /** SHARED temperature rows, owned by TelemetryStoreService. */
  get temperatureDataRows(): ReadonlyTelemetryBuffer<TemperatureDataRow> {
    return this.telemetryStore.read<TemperatureDataRow>('temperature');
  }

  /** Absolute index of the newest row already projected into the waterfall. */
  private lastWaterfallIndex = -1;
  private tempChangeSub?: Subscription;
  xyDataSeries3dLeft: XyDataSeries = {} as XyDataSeries;
  tick: number = 0;
  tickMax: number = 0;
  markerPosition3d: number = 0;
  tempData: ProjectTemperatureData = {} as ProjectTemperatureData;
  // Used for Vertical Time Labels
  temp_startUnixTime: number = 0;
  sciChartSurface_left?: SciChartSurface; // Store reference to left chart surface
  sciChartSurface_right?: SciChartSurface; // Store reference to right chart surface
  isInitialLoad: boolean = true; // Track if this is the first load

  // Zone Chart
  /** Bounded zone row storage — see TelemetryRingBuffer. */
  /**
   * SHARED zone rows, owned by TelemetryStoreService.
   *
   * This tab only ever counted them: it pushed RT rows in and read totalCount /
   * retainedCount for a log line, never rendering from them and never loading
   * the backfill. So sharing costs it nothing and incidentally gives it the
   * historical rows it never had.
   */
  get zoneDataRows(): ReadonlyTelemetryBuffer<ZoneDataRow> {
    return this.telemetryStore.read<ZoneDataRow>('zone');
  }
  heatmapDataSeries1: UniformHeatmapDataSeries = {} as UniformHeatmapDataSeries;

  // Fault overlay (Desktop AreaFiberFaultAnnotation parity on the waterfall)
  lossData: ProjectLossData = {} as ProjectLossData;
  /** Bounded loss-row storage — see TelemetryRingBuffer. Absolute tick index. */
  /**
   * SHARED loss rows, owned by TelemetryStoreService.
   *
   * No changed$ handler here: this tab's fault-span fold derives from the
   * PAYLOAD (data.LRC, the full backfill from tick 0) rather than from the
   * retained buffer — deliberately, so the fold cannot restart mid-cure. It
   * therefore stays in the payload subscription where it already was.
   */
  get lossDataRows(): ReadonlyTelemetryBuffer<LossDataRow> {
    return this.telemetryStore.read<LossDataRow>('loss');
  }
  /**
   * Incremental fault-span state. deriveFaultSpans() used to be re-run over the
   * whole loss history on every poll; this folds one tick at a time.
   */
  private faultSpanAcc = new FaultSpanAccumulator();
  faultSpans: FaultSpan[] = [];

  /**
   * One latch per surface: zooming the marker chart must not freeze the
   * waterfall's fit, and vice versa. They are independent charts.
   */
  private zoomLatch = new ZoomLatch();
  private zoomLatch2 = new ZoomLatch();
  private programmaticRangeChange = false;
  private dblClickBound = new Set<string>();
  /**
   * The latch may not arm until the chart is built.
   *
   * Adding an axis, and SciChart's default autoRange firing when a series first
   * receives data, both emit visibleRangeChanged — with the programmatic flag
   * clear, because neither is us setting a range. Subscribing before
   * `xAxes.add` therefore armed the latch during construction and suspended
   * every automatic fit for the lifetime of the chart, leaving the axes on their
   * 0-10 defaults. /temperature escaped only because it happens to subscribe
   * after the add.
   *
   * Ordering alone is too subtle to rely on, so this is the belt: a range change
   * is only a user gesture once construction has settled on a later macrotask.
   */
  private chartInteractive = false;

  private armZoomLatch(latch: ZoomLatch): void {
    if (!this.chartInteractive) {
      return;
    }
    latch.noteRangeChange(this.programmaticRangeChange);
  }

  waterfallFaultAnnotations: BoxAnnotation[] = [];

  rolloverModifier_left?: RolloverModifier;
  rolloverModifier?: RolloverModifier;
  zoomPanModifier?: ZoomPanModifier;
  rubberBandZoomModifier?: RubberBandXyZoomModifier;

  // TODO: get from Api
  // channelrange_start: number = 0;
  // channelrange_end: number = 37;
  // channelrange_span: number = 37;
  // samplingInterval: number = 0.15;
  // measurementInterval: number = 1;
  channelrange_start: number = 0;
  channelrange_end: number = 4;
  channelrange_span: number = 4;
  samplingInterval: number = 1;
  measurementInterval: number = 1;
  /** No marker produced a series, so the temp-over-time pane has nothing to draw. */
  noMarkerSeries = false;
  /**
   * Waterfall z-matrix, one row per retained tick. Kept dense because
   * UniformHeatmapDataSeries.setZValues() needs a contiguous number[][], but
   * bounded to WATERFALL_MAX_ROWS and appended to incrementally.
   */
  heatMaps: number[][] = [];
  private readonly WATERFALL_MAX_ROWS = DEFAULT_TICK_RETENTION;
  /** Marker series retained so their WASM memory can be released before rebuild. */
  private markerDataSeries: XyDataSeries[] = [];
  /** Waterfall surface, retained so its renderable series can be released. */
  private waterfallSurface?: SciChartSurface;
  /** Kept so the waterfall can be rebuilt from a store notification. */
  private waterfallWasm?: TSciChart;
  isRunning: boolean = false;

  rangeSliderLeftVal: number = 0;
  rangeSliderRightVal: number = 100;
  left_rangeSliderTopVal: number = 0;
  left_rangeSliderBotVal: number = 100;
  right_rangeSliderLeftVal: number = 0;
  right_rangeSliderRightVal: number = 100;

  // Chart Controls Events
  public onZoomOut?: () => void;
  public onAddMarker?: () => void;
  /**
   * ONE REDRAW PER FRAME FOR THE RANGE SLIDERS.
   *
   * `<input type="range">` raises `input` several times per animation frame, and
   * each one ran a full visible-range assignment straight through to SciChart. A
   * redraw here is measured at ~75 ms and does NOT scale with cure length, so at
   * several events per frame against a 16 ms budget the main thread falls
   * permanently behind and the event queue grows without bound — which is the
   * "this page isn't responding" tab kill reported from the bench while dragging
   * the bottom time slider on this screen.
   *
   * DROP, DON'T QUEUE — the newest slider position wins and the intermediate ones
   * are discarded, because they are exactly the positions nobody sees. This is
   * also what the desktop does: GraphViewModel.InternalUpdate keeps at most ONE
   * pending follow-up and drops every further request until it completes.
   */
  private readonly bottomRangeFrames = new FrameCoalescer<[number, number]>(
    ([l, r]) => this.onRangeSliderChange?.(l, r)
  );
  private readonly leftRangeFrames = new FrameCoalescer<[number, number]>(
    ([t, b]) => this.left_onRangeSliderChange?.(t, b)
  );
  private readonly rightRangeFrames = new FrameCoalescer<[number, number]>(
    ([t, b]) => this.right_onRangeSliderChange?.(t, b)
  );
  private readonly right2RangeFrames = new FrameCoalescer<[number, number]>(
    ([t, b]) => this.right2_onRangeSliderChange?.(t, b)
  );

  public onRangeSliderChange?: (left: number, right: number) => void;
  public left_onRangeSliderChange?: (left: number, right: number) => void;
  public right_onRangeSliderChange?: (left: number, right: number) => void;
  public right2_onRangeSliderChange?: (left: number, right: number) => void;

  selectedProjectSubscription?: Subscription;
  tempDataSubscription?: Subscription;
  tempRTDataSubscription?: Subscription;
  zoneRTDataSubscription?: Subscription;
  lossDataSubscription?: Subscription;
  lossRTDataSubscription?: Subscription;
  tickSubscription?: Subscription;
  vert_annot_positionsSubscription?: Subscription;
  markers: MarkerInfo[] = [];

  showRangeSlider: boolean = false;

  constructor(
    public projectService: ProjectService,
    private chartCapture: ChartCaptureService,
    private adaptive: AdaptiveResolutionService,
    private chartTick: ChartTickService,
    private telemetryStore: TelemetryStoreService
  ) {}

  ngOnInit() {
    // ONE SHARED TICK. Registration IS the wiring — see ChartTickService.
    this.unregisterTick = this.chartTick.register((tick) => this.onSharedTick(tick));
    // Reads the shared zone rows, so it must ensure the family is fed even when
    // /temperature was never opened. connect() is idempotent.
    this.telemetryStore.connect('zone');
    this.telemetryStore.connect('temperature');
    this.telemetryStore.connect('loss');
    this.telemetryStore.requireRetention('loss', 'graph', DEFAULT_TICK_RETENTION);
    this.telemetryStore.requireRetention('temperature', 'graph', DEFAULT_TICK_RETENTION);
    this.projectService.isHelpOverlayVisible = false;

    // Store wiring above stays synchronous; everything that can touch SciChart
    // moves into initialise(). See TemperatureComponent.initialise() for why.
    void this.initialise();
  }

  /**
   * Surfaces first, subscriptions second.
   *
   * SciChart is dynamically imported, so createTempAndWaterfallGraphs() after an
   * un-awaited initSciChart() would have run against classes that did not exist
   * yet, and changed$ (a ReplaySubject(1)) fires onTemperatureRowsChanged
   * synchronously on subscribe. Both now happen after the loader resolves; the
   * replay means the deferred subscription still sees the latest change.
   */
  private async initialise(): Promise<void> {
    try {
      await this.initSciChart();
      this.chartsReady = true;
      // Axis labels are baked in from isMetricUnit at construction. Without
      // this the cold-start path builds metric axes and then binds imperial
      // data into them — see ProjectService.whenUnitsSettled.
      await this.projectService.whenUnitsSettled();
      await this.createTempAndWaterfallGraphs();
      this.createTempLegend();
    } catch (err) {
      console.error('Graph > chart initialisation failed', err);
    }

    this.tempChangeSub = this.telemetryStore
      .changed$('temperature')
      .subscribe((change) => this.onTemperatureRowsChanged(change));
    this.setupProjectSubscription();
  }

  /** Drops this surface out of the shared tick fan-out. */
  private unregisterTick?: () => void;

  /**
   * TEARDOWN CANNOT BE ALLOWED TO ABORT A NAVIGATION.
   *
   * Angular calls this while DEACTIVATING the route, so anything that escapes
   * here fails the navigation and the user simply does not move — which is
   * what "clicking a tab takes me back to temperature" actually was. Worse,
   * the surfaces above are already deleted by then, so the route you are stuck
   * on is holding dead charts. See safe-teardown.ts for the full stack.
   *
   * A leaked timer is a leak. A broken tab strip is the app not working.
   */
  ngOnDestroy(): void {
    runTeardown('GraphComponent.ngOnDestroy', () => {
      this.unregisterTick?.();
      // Drop any slider frame scheduled but not yet run. Without this a queued
      // callback assigns a visibleRange on a surface that is being deleted, and
      // SciChart throws on a deleted context — visible only as a console error
      // during navigation.
      this.bottomRangeFrames.cancel();
      this.leftRangeFrames.cancel();
      this.rightRangeFrames.cancel();
      this.right2RangeFrames.cancel();

      // Release data series before the surfaces that own them.
      for (const stale of this.markerDataSeries) {
        try {
          stale.delete();
        } catch {
          // already deleted
        }
      }
      this.markerDataSeries = [];
      this.disposeWaterfallSeries();

      // Dispose of the chart surfaces to free up resources
      if (this.sciChartSurface_left) {
        this.chartCapture.unregister('temperatureOverTime', this.sciChartSurface_left);
        this.sciChartSurface_left.delete();
      }
      if (this.sciChartSurface_right) {
        this.chartCapture.unregister('heatmap3D', this.sciChartSurface_right);
        this.sciChartSurface_right.delete();
      }
    
      closeSubscription('selectedProjectSubscription', this.selectedProjectSubscription);
      closeSubscription('tempDataSubscription', this.tempDataSubscription);
  this.tempChangeSub?.unsubscribe();
      this.telemetryStore.releaseRetention('temperature', 'graph');
      this.telemetryStore.releaseRetention('loss', 'graph');
          closeSubscription('tempRTDataSubscription', this.tempRTDataSubscription);
      closeSubscription('zoneRTDataSubscription', this.zoneRTDataSubscription);
      closeSubscription('lossDataSubscription', this.lossDataSubscription);
      closeSubscription('lossRTDataSubscription', this.lossRTDataSubscription);
      //this.tickSubscription.unsubscribe();
      closeSubscription('vert_annot_positionsSubscription', this.vert_annot_positionsSubscription);

      this.faultSpanAcc.reset();
      this.heatMaps = [];
      this.waterfallSurface = undefined;
    });
  }

  // Setup subscription to recreate chart when project changes
  setupProjectSubscription() {
    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (projectName) => {
          if (projectName && projectName.length > 0) {
            console.log('Graph > Project changed to:', projectName);
            console.log('Graph > Current unit setting:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
            
            // Skip recreation on initial load (graph is already being created in ngOnInit)
            if (this.isInitialLoad) {
              this.isInitialLoad = false;
              console.log('Graph > Skipping recreation on initial load');
              return;
            }
            
            // Recreate the graphs with the new project's unit settings
            this.recreateGraphs();
          }
        },
        (error) => (this.errorMessage = error)
      );
  }

  // Recreate the graphs (called when project changes)
  async recreateGraphs() {
    console.log('Graph > Recreating graphs with units:', this.projectService.isMetricUnit ? 'Metric' : 'Imperial');
    
    // Dispose of the existing charts if they exist
    if (this.sciChartSurface_left) {
      console.log('Graph > Disposing existing left chart');
      this.sciChartSurface_left.delete();
      this.sciChartSurface_left = undefined;
    }
    if (this.sciChartSurface_right) {
      console.log('Graph > Disposing existing right chart');
      this.sciChartSurface_right.delete();
      this.sciChartSurface_right = undefined;
    }

    // RESET THE ZOOM LATCHES. A new project frames a different liner, and the
    // axis the user zoomed no longer exists — it was deleted three lines up.
    //
    // Without this the latch stays set from the PREVIOUS project, and every
    // `if (!this.zoomLatch.isZoomed) zoomExtents()` on data arrival is skipped,
    // so the new project's data never re-frames the axis.
    //
    // WHAT ARMS THE LATCH, precisely. An earlier version of this comment implied a
    // bare project switch was enough. It is not. `armZoomLatch` latches on any
    // visibleRangeChanged where `programmaticRangeChange` is false, and that flag
    // is set true in exactly two places, both on the zoomExtents-on-data-arrival
    // path. So THE BOTTOM RANGE SLIDER ARMS IT: `onRangeSliderChange` writes
    // `xAxis2.visibleRange` with no flag, and xAxis2 belongs to the waterfall, so
    // an ordinary slider drag sets zoomLatch2. No zoom tool is involved, which is
    // why looking for a zoom gesture finds nothing.
    //
    // NOT DEMONSTRATED — recorded as such on purpose. The reported symptom was a
    // 200 ft project followed by a 120 ft one leaving the top strip at 0–200 ft,
    // but that report omitted the arming step and has since been retracted by the
    // reporter. The picker is its own ROUTE (/projects), so switching through it
    // destroys this component and a fresh one starts unlatched. The in-place paths
    // that reach recreateGraphs while the component lives — upload-complete and
    // restore — are where this can actually bite. So treat the reset as closing a
    // contract gap (ZoomLatch's own doc: "A project change MUST reset"), NOT as a
    // fix for a reproduced user report, until someone drives it.
    //
    // temperature.component's recreateTempGraph has done this since its own
    // pinning fix and carries the same reasoning in a comment; /graph was simply
    // never given the equivalent. Both latches, because the waterfall has its
    // own.
    this.zoomLatch.reset();
    this.zoomLatch2.reset();

    // Clear data
    this.heatMaps = [];
    this.heatmapDataSeries1 = {} as UniformHeatmapDataSeries;

    // Recreate the charts
    await this.createTempAndWaterfallGraphs();
    await this.createTempLegend();
  }

  async initSciChart() {
    // SciChart is dynamically imported — nothing below exists until this
    // resolves. This is the only place chart objects are constructed, so it is
    // the only place that needs to wait.
    await loadSciChart();
    SciChartSurface.useWasmFromCDN();
    SciChartSurface.setRuntimeLicenseKey(environment.sciChartKey);
  }

  // Temperatere Graph ===========================================================================

  async createTempAndWaterfallGraphs() {
    console.log('Graph > Creating graphs with units:', this.projectService.isMetricUnit ? 'Metric (m, °C)' : 'Imperial (ft, °F)');
    
    // Left Temp Over Time Graph
    const { sciChartSurface, wasmContext } = await SciChartSurface.create(
      this.tempOverTimeGraphId
    );

    // Store reference to the left chart surface
    this.sciChartSurface_left = sciChartSurface;
    this.chartCapture.register('temperatureOverTime', sciChartSurface, this.tempOverTimeGraphId);

    // X axis = Temperature (bottom), Y axis = Time (left)
    //
    // SWAPPED to match viscom_liner, which plots this panel with time running up
    // the vertical axis and temperature across. web.ui had the transpose: time on
    // X, temperature on Y. Both the axis CONFIGURATION and the data are swapped
    // together — moving only one would have produced a chart that is merely
    // mislabelled rather than reoriented.
    const xAxis = new NumericAxis(wasmContext, {
      autoRange: EAutoRange.Always,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
      drawLabels: true,
      axisAlignment: EAxisAlignment.Bottom,
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    // growBy follows the temperature axis, where it was, so the trace keeps its
    // breathing room on the axis that actually varies.
    xAxis.growBy = new NumberRange(0.2, 0.2);

    const yAxis = new NumericAxis(wasmContext, {
      autoRange: EAutoRange.Once,
      // SCALE HIDDEN ON THIS CHART, AXIS KEPT.
      //
      // Joel: "once we get this flipped and confirm the left and right charts still
      // share Y axis scale, I'd like to hide the Y axis scale from the left chart on
      // /graph." BOTH gates passed before this landed, driven on prod2 rather than
      // asserted: the shared range showed 0:00:00-9:43:20 on both charts, and the
      // flip was confirmed RENDERING — time downward, 0:00:00 at the top — not
      // merely present in the bundle. Hiding a scale that pointed the wrong way
      // would have hidden the evidence of the bug instead of fixing it.
      //
      // PARITY, not tidying: desktop's multi-view puts ONE time axis column BETWEEN
      // the two charts, a single axis object serving both rather than two.
      //
      // drawLabels, NOT isVisible. The axis still has to exist to position the
      // series and to carry the shared time range the scrubber drives; hiding the
      // axis itself would take the geometry with it. This hides only the scale, and
      // the waterfall's axis beside it still shows the numbers for both.
      drawLabels: false,
      // TIME RUNS DOWNWARD, oldest at the top — see the note on yAxis2 below.
      // BOTH time axes on this screen carry this, so they still agree.
      flippedCoordinates: true,
      axisAlignment: EAxisAlignment.Left,
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    yAxis.labelProvider.formatLabel = this.formatDurationInSecToHHMMSS_Custom2;

    sciChartSurface.xAxes.add(xAxis);
    xAxis.visibleRangeChanged.subscribe(() =>
      this.armZoomLatch(this.zoomLatch)
    );
    setTimeout(() => (this.chartInteractive = true), 0);
    sciChartSurface.yAxes.add(yAxis);

    // NO MARKER LEGEND ON THIS SURFACE.
    //
    // Joel: "the marker legend is not present on desktop and shouldn't be on webui
    // either. this is for the left graph on the /graph tab." Confirmed from his
    // screen recording: there is no marker legend anywhere on desktop. The only
    // legend is the SERIES legend on the curing chart — the four heat-input bands
    // plus current and maximal temperature, each with a checkbox.
    //
    // Desktop has none here for a structural reason, not a styling one: it sets NO
    // SeriesName on marker series (GraphViewModel.cs:1580), and no name means no
    // legend entry. That is the SAME root fact behind the tooltip defect, where web
    // printed the series name — "Marker at 60.8m" — where a temperature belonged.
    // Both are "stop treating the marker's length as its identity text".
    //
    // Markers stay VISIBLE: the vertical lines and their coloured axis labels are
    // correct and desktop has them. Only the legend block goes. The series keep
    // their dataSeriesName because the rollover no longer reads it and removing it
    // would change nothing a user can see.

    const cursorModifier = new CursorModifier({
      crosshairStroke: '#ff6600',
      crosshairStrokeThickness: 1,
      tooltipContainerBackground: '#000',
      tooltipTextStroke: '#ff6600',
      //showTooltip: true,
      axisLabelFill: '#b36200',
      axisLabelStroke: '#fff',
      //showTooltip: false,
      showYLine: false,
    });

    sciChartSurface.chartModifiers.add(cursorModifier);

    const customTemplate = (seriesInfo: any) => this.markerTooltipLines(seriesInfo);

    // THIS TOOLTIP CANNOT BE TESTED IN KARMA. Verify it in a real browser.
    //
    // Synthetic PointerEvents do not reliably drive SciChart's modifiers, so a
    // spec that hovers a surface and reads the tooltip is measuring the harness
    // rather than this code. That is not a guess: marker-rollover-live.spec.ts
    // asserted exactly this and passed on scichart 3.1.348, then on 5.2.69 read
    // "86.04°F" — two decimals, which markerTooltipLines' toFixed(1) cannot
    // produce and the axis label provider can. It had been reporting SciChart's
    // DEFAULT renderer as ours. Deleted in 527594f; see karma.gl.conf.js, which
    // now says what that config does and does not give you.
    //
    // WHAT THE REAL BROWSER SHOWS, on the deployed beta-dev bundle (S3640 REV_1,
    // scichart 5.2.69): 108.9°F in a red box on the red trace, 108.9°F in a green
    // box on the green trace — one decimal, no series name, colour-matched,
    // beside an axis label reading 109.02°F in the same frame. The template below
    // IS in the render path.
    //
    // Create new modifier instance to avoid "already attached" errors
    this.rolloverModifier_left = new RolloverModifier({
      tooltipDataTemplate: customTemplate,
      // MEASURED NO-OP, KEPT ONLY BECAUSE IT IS THE LIBRARY DEFAULT.
      //
      // This was committed claiming it bought "all series at once". It does not:
      // flipping it to false changed nothing measurable, and the PRE-FIX code
      // showed the same count. Whatever made Rob see a single tooltip, it was
      // never this flag.
      //
      // THE MECHANISM BEHIND IT IS UNSETTLED ON THE CURRENT ENGINE, and the
      // earlier account here is withdrawn rather than corrected. It described
      // RolloverModifier hit-testing an X-SLICE and parking un-hit boxes
      // off-canvas — read from SciChart 3 source and measured on a SciChart 3
      // build. The app is on 5.2.69 now and the only evidence gathered on 5 is
      // from the DEPLOYED beta-dev bundle: S3640 REV_1, two marker traces, two
      // visible colour-matched boxes. That says nothing about what 5 does with a
      // series the slice does not reach, so no mechanism is asserted here.
      //
      // The template itself IS in the render path on 5 — same screenshot shows
      // the tooltip at 108.9°F (our toFixed(1)) beside an axis label at 109.02°F
      // (the provider's two decimals), which the default renderer cannot produce.
      showTooltip: true,
    });
    sciChartSurface.chartModifiers.add(this.rolloverModifier_left);

    // Right Waterfall Graph ========================================================

    const { sciChartSurface: sciChartSurface2, wasmContext: wasmContext2 } =
      await SciChartSurface.create(this.waterfallGraphId);

    // Store reference to the right chart surface
    this.sciChartSurface_right = sciChartSurface2;
    this.chartCapture.register('heatmap3D', sciChartSurface2, this.waterfallGraphId);

    // THE SCALE THAT TELLS YOU WHAT THE COLOURS MEAN. On screen it is the
    // HeatmapLegend in #temp-legend, a sibling div — so it is not part of this
    // surface and was absent from every report ever generated. Rob: "there's no
    // X temperature gauge over here for colors." The capture draws it from this
    // spec into the right margin of the same image.
    this.chartCapture.registerColorScale('heatmap3D', () => ({
      title: 'Temperature',
      min: this.projectService.unit_Temp_Val(WATERFALL_TEMP_DOMAIN_C.min),
      max: this.projectService.unit_Temp_Val(WATERFALL_TEMP_DOMAIN_C.max),
      unitSuffix: this.projectService.isMetricUnit ? '\u00B0C' : '\u00B0F',
      stops: WATERFALL_TEMP_GRADIENT_STOPS,
    }));

    // Create an X,Y Axis and add to the chart
    const xAxis2 = new NumericAxis(wasmContext2, {
      autoRange: EAutoRange.Once,
      drawLabels: true,
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 2,
      labelPostfix: this.projectService.isMetricUnit ? 'm' : 'ft',
      // NICE NUMBERS, not a fixed 20 m interval. viscom_liner uses no custom
      // tick logic — SciChart's default provider picks a delta of 1/2/5 x 10^n
      // with MaxAutoTicks 10. A hardcoded majorDelta gave 20 m ticks whatever the
      // liner length, so a short liner drew two labels and a long one drew forty.
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    const yAxis2 = new NumericAxis(wasmContext2, {
      autoRange: EAutoRange.Once,
      drawLabels: true,
      // TIME RUNS DOWNWARD: 00:00:00 at the TOP, the newest tick at the BOTTOM.
      //
      // HISTORY, because this reverses an earlier deliberate change and the reason
      // matters. This axis alone used to carry flippedCoordinates, which put t=0 at
      // the top HERE and at the bottom on the left chart — two time axes side by
      // side disagreeing about which way time runs. That is genuinely unusable and
      // the fix removed the flip, on the stated reading that desktop runs time UP.
      //
      // THAT READING WAS WRONG, and Joel's own desktop captures settle it: the
      // desktop waterfall shows 00:00:00 at the TOP and 04:15:44 at the BOTTOM.
      // Web had 0:00:00 at the bottom and 4:26:40 at the top — inverted. This is
      // also the "upside down chart" reported days ago and closed as resolved when
      // it was not.
      //
      // So the earlier fix's INVARIANT is kept and only its direction changes: both
      // axes flip together, so they still agree with each other, and now they agree
      // with desktop too. Keeping them consistent is what makes hiding one of them
      // possible at all.
      flippedCoordinates: true,
      axisAlignment: EAxisAlignment.Left,
      // Time axis of the swapped Marker chart — same nice-number budget as the
      // rest. Its labels are HH:MM:SS, so an unbudgeted axis crowds fastest here.
      maxAutoTicks: MAX_AUTO_TICKS,
      majorGridLineStyle: GRIDLINE_STYLE.major,
      minorGridLineStyle: GRIDLINE_STYLE.minor,
    });

    yAxis2.labelProvider.formatLabel = this.formatDurationInSecToHHMMSS_Custom2;

    sciChartSurface2.xAxes.add(xAxis2);
    xAxis2.visibleRangeChanged.subscribe(() =>
      this.armZoomLatch(this.zoomLatch2)
    );
    // Retained so the SHARED TICK can move the time window on both surfaces.
    this.timeAxes = [yAxis, yAxis2];
    setTimeout(() => (this.chartInteractive = true), 0);
    sciChartSurface2.yAxes.add(yAxis2);

    // Event Subscriptions =======================================================
    // Note: Don't subscribe to selectedProject here as it's handled in setupProjectSubscription()

    // BIND THE WATERFALL SURFACE HERE, NOT INSIDE THE DATA HANDLER.
    //
    // These used to be assigned in the temperatureData handler — below three
    // early returns (null data, null TRC, empty TRC). onTemperatureRowsChanged
    // refuses to do anything without them, so whenever that handler returned
    // early the rows-changed hook was inert, and rows landing afterwards
    // notified a listener that immediately gave up. Nothing rebuilt, and the
    // waterfall kept whatever the empty first pass had produced.
    //
    // That is the reload-dependent render: on a full reload temperatureData
    // still holds its empty opening value when the charts are built, so the
    // handler returns early; the surface reference is the one thing that must
    // NOT depend on data having arrived, because it is what lets the arrival be
    // noticed at all.
    this.waterfallSurface = sciChartSurface2;
    this.waterfallWasm = wasmContext2;

    // Unsubscribe from previous subscriptions if they exist to prevent memory leaks
    if (this.tempDataSubscription && typeof this.tempDataSubscription.unsubscribe === 'function') {
      this.tempDataSubscription.unsubscribe();
    }
    this.tempDataSubscription = this.projectService.temperatureData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Graph Comp: Temp data is null.');
          return;
        } else if (data.TRC == null) {
          console.log('Error in Graph Comp: Temp data > TRC is null.');
          return;
        } else if (data.TRC.length == 0) {
          console.log(
            'Error in Graph Comp: Temp data > TRC collection has no value.'
          );
          return;
        }

        this.tempData = data;
        this.samplingInterval = data.SI;
        this.measurementInterval = data.MI;
        this.channelrange_start = data.CRB;
        this.channelrange_end = data.CRE;
        this.channelrange_span = data.CRS;

        //console.log('projectService.temperatureData.subscribe. Tick to zero');
        this.updateTempChartForMarkers(sciChartSurface, wasmContext);
        // Not while the user is zoomed — this runs on every data arrival.
        if (!this.zoomLatch.isZoomed) {
          this.programmaticRangeChange = true;
          try {
            sciChartSurface.zoomExtents();
          } finally {
            setTimeout(() => (this.programmaticRangeChange = false), 0);
          }
        }

        // Waterfall Graph Setup Data Refresh ===================================
        this.waterfallSurface = sciChartSurface2;
        this.waterfallWasm = wasmContext2;
        this.rebuildWaterfallChart(sciChartSurface2, wasmContext2);
        this.lastWaterfallIndex = this.temperatureDataRows.nextIndex - 1;
        // The attach now lives in rebuildWaterfallChart, paired with the dispose.
        this.reframeWaterfallExtent(xAxis2);
      },
      (error) => (this.errorMessage = error)
    );

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.tempRTDataSubscription && typeof this.tempRTDataSubscription.unsubscribe === 'function') {
      this.tempRTDataSubscription.unsubscribe();
    }
    this.tempRTDataSubscription =
      this.projectService.temperatureRTData.subscribe(
        (data) => {
          console.log('Graph comp > temperatureRTData.subscribe');
          if (data == null) {
            console.log('Error in Graph Comp: Temp RT data is null.');
            return;
          } else if (data.TRC == null) {
            console.log('Error in Graph Comp: Temp RT data > TRC is null.');
            return;
          }

          if (data.TRC.length > 0) {
            // Ingest every returned row, not just [0].
            for (const row of data.TRC) {
            }

            console.log(
              'Graph comp > pushed ' +
                data.TRC.length +
                ' row(s). Ticks: ' +
                this.temperatureDataRows.totalCount +
                ' retained: ' +
                this.temperatureDataRows.retainedCount
            );

            this.updateTempChartForMarkers(sciChartSurface, wasmContext);
            if (!this.zoomLatch.isZoomed) {
              this.programmaticRangeChange = true;
              try {
                sciChartSurface.zoomExtents();
              } finally {
                setTimeout(() => (this.programmaticRangeChange = false), 0);
              }
            }

            // Incremental waterfall append — no full-matrix rebuild, no new series.
          }
        },
        (error) => (this.errorMessage = error)
      );

    // Zone RT Data Subscription for Heatmap/Waterfall updates
    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.zoneRTDataSubscription && typeof this.zoneRTDataSubscription.unsubscribe === 'function') {
      this.zoneRTDataSubscription.unsubscribe();
    }
    this.zoneRTDataSubscription = this.projectService.zoneRTData.subscribe(
      (data) => {
        console.log('Graph comp > zoneRTData.subscribe');
        if (data == null) {
          console.log('Error in Graph Comp: Zone RT data is null.');
          return;
        } else if (data.ZRC == null) {
          console.log('Error in Graph Comp: Zone RT data > ZRC is null.');
          return;
        }

        if (data.ZRC.length > 0) {
          // Ingest every returned row, not just [0].
          for (const row of data.ZRC) {
          }

          console.log(
            'Graph comp > pushed ' +
              data.ZRC.length +
              ' zone row(s). Ticks: ' +
              this.zoneDataRows.totalCount +
              ' retained: ' +
              this.zoneDataRows.retainedCount
          );

          // The waterfall is driven by TEMPERATURE rows and is appended to in the
          // temperature RT handler. Rebuilding it here duplicated that work and
          // added a second renderable series to the surface on every poll.
        }
      },
      (error) => (this.errorMessage = error)
    );

    // Loss data subscriptions — feed the waterfall fault overlay (Desktop
    // AreaFiberFaultAnnotation parity). Fault detection→repair spans are derived
    // from the per-tick FD/FP/FM snapshots (see deriveFaultSpans for the
    // documented approximation).
    if (this.lossDataSubscription && typeof this.lossDataSubscription.unsubscribe === 'function') {
      this.lossDataSubscription.unsubscribe();
    }
    this.lossDataSubscription = this.projectService.lossData.subscribe(
      (data) => {
        if (data == null || data.LRC == null || data.LRC.length == 0) {
          return;
        }
        this.lossData = data;
        // Copy — health.component pushes RT rows into the shared envelope array;
        // during offscreen export both components can be alive at once.
        // Rebuild the span fold from the FULL backfill (from tick 0) — the ring
        // buffer only retains a window, but the fold must not restart mid-cure.
        this.faultSpanAcc.rebuildFrom(data.LRC);
        this.faultSpans = this.faultSpanAcc.spans;
        this.updateWaterfallFaultAnnotations(sciChartSurface2);
      },
      (error) => (this.errorMessage = error)
    );

    if (this.lossRTDataSubscription && typeof this.lossRTDataSubscription.unsubscribe === 'function') {
      this.lossRTDataSubscription.unsubscribe();
    }
    this.lossRTDataSubscription = this.projectService.lossRTData.subscribe(
      (data) => {
        if (data == null || data.LRC == null || data.LRC.length == 0) {
          return;
        }
        // Ingest every returned row, not just [0], and advance the span fold
        // incrementally — O(faults this tick) instead of O(ticks) per poll.
        for (const lr of data.LRC) {
          this.faultSpanAcc.foldRow(lr);
        }
        this.faultSpans = this.faultSpanAcc.spans;
        this.updateWaterfallFaultAnnotations(sciChartSurface2);
      },
      (error) => (this.errorMessage = error)
    );

    // this.tickSubscription = this.projectService.tickPosition.subscribe(
    //   (data) => {
    //     if (this.temperatureDataRows.length > 0) {
    //       const endPosition =
    //         data == 0 ? this.temperatureDataRows.length - 1 : data;

    //       const endTimeDuration = this.getTimeDiffInSec(
    //         this.temperatureDataRows[0].UT,
    //         this.temperatureDataRows[endPosition].UT
    //       );

    //       // Temp Graph Navigation
    //       xAxis.visibleRange = new NumberRange(0, endTimeDuration);

    //       // Waterfall Graph Navigation
    //       yAxis2.visibleRange = new NumberRange(0, endTimeDuration);
    //     }
    //   },
    //   (error) => (this.errorMessage = error)
    // );

    // ONE TIME RANGE, TWO CHARTS.
    //
    // Both vertical axes are time over the same cure, so any moment when they
    // disagree is a moment one of them is lying. Desktop shares the ranges
    // themselves rather than merely the tick, and this is that: a change to
    // either is applied to the other, which is also what makes hiding one of the
    // two axes safe — a hidden axis that could drift would silently mislabel the
    // chart it belongs to.
    //
    // The guard is what keeps this from ringing: SciChart raises
    // visibleRangeChanged for programmatic assignments too, so without it each
    // side would answer the other indefinitely.
    const shareTimeRange = createRangeSharer(
      (min, max) => new NumberRange(min, max)
    );
    yAxis.visibleRangeChanged.subscribe(() => shareTimeRange(yAxis, yAxis2));
    yAxis2.visibleRangeChanged.subscribe(() => shareTimeRange(yAxis2, yAxis));

    // Listen to Control Events:
    this.onZoomOut = () => {
      // Hand both surfaces back to automatic fitting.
      this.zoomLatch.reset();
      this.zoomLatch2.reset();
      this.rangeSliderLeftVal = 0;
      this.rangeSliderRightVal = 100;

      xAxis2.visibleRange = new NumberRange(
        0,
        this.channelrange_end - this.channelrange_start
      );

      yAxis2.visibleRange = new NumberRange(0, this.retainedTimeSpanSec());
    };

    // Double-click resets to extents, as the desktop does.
    //
    // THE IDS MUST COME FROM THE COMPONENT, NOT FROM LITERALS. They became
    // per-instance when the export's offscreen prerender stopped colliding with
    // the mounted copy, so getElementById('temp-over-time-graph') now returns
    // null — and the `if (host && ...)` below swallows it. No error, no warning:
    // the listener never binds and double-click reset silently stops working.
    for (const id of [this.tempOverTimeGraphId, this.waterfallGraphId]) {
      const host = document.getElementById(id);
      if (host && !this.dblClickBound.has(id)) {
        this.dblClickBound.add(id);
        host.addEventListener('dblclick', () => this.zoomOut());
      }
    }

    this.onRangeSliderChange = (left, right) => {
      //console.log('bottom:' + left + '-' + right);
      const linerLength = this.tempData.CRE - this.tempData.CRB;
      const visibleLeft = Math.round((left / 100) * linerLength);
      const visibleRight = Math.round((right / 100) * linerLength);
      xAxis2.visibleRange = new NumberRange(visibleLeft, visibleRight);
    };

    this.left_onRangeSliderChange = (top, bot) => {
      //console.log('left:' + top + '-' + bot);
      if (this.temperatureDataRows.retainedCount > 0) {
        const endTimeDuration = this.retainedTimeSpanSec();

        const visibleLeft = Math.round((top / 100) * endTimeDuration);
        const visibleRight = Math.round((bot / 100) * endTimeDuration);
        // yAxis, NOT xAxis. This panel was swapped so that time runs up the
        // vertical axis and temperature across the bottom; the slider kept
        // driving xAxis afterwards, which meant dragging the TIME slider rescaled
        // the TEMPERATURE axis by a number of seconds. That is how the left chart
        // ended up showing temperature ranges nothing had measured.
        yAxis.visibleRange = new NumberRange(visibleLeft, visibleRight);
        yAxis2.visibleRange = new NumberRange(visibleLeft, visibleRight);
      }
    };

    // Unsubscribe from previous subscription if it exists to prevent memory leaks
    if (this.vert_annot_positionsSubscription && typeof this.vert_annot_positionsSubscription.unsubscribe === 'function') {
      this.vert_annot_positionsSubscription.unsubscribe();
    }
    this.vert_annot_positionsSubscription =
      this.projectService.vert_annot_positions.subscribe(
        (data) => {
          this.markers = data;
          this.updateVerticalAnnotations(sciChartSurface2, data);
          this.updateTempChartForMarkers(sciChartSurface, wasmContext);
        },
        (error) => (this.errorMessage = error)
      );

    this.onAddMarker = () => {
      this.projectService.addMarker();
    };
  }

  /** Wall-clock span of the retained window, in seconds. */
  private retainedTimeSpanSec(): number {
    const first = this.temperatureDataRows.get(this.temperatureDataRows.firstIndex);
    const last = this.temperatureDataRows.last;
    if (!first || !last) return 0;
    return this.getTimeDiffInSec(first.UT, last.UT);
  }

  updateTempChartForMarkers(sciChart: SciChartSurface, wasmContext: TSciChart) {
    if (this.temperatureDataRows.retainedCount == 0) {
      this.tempDataLoaded = false;
      return;
    } else {
      this.tempDataLoaded = true;
    }

    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    const positions = this.projectService.getVerticalAnnotationPositions();

    // renderableSeries.clear() detaches the visuals but leaves the WASM-backed
    // XyDataSeries allocated; this ran once per poll, so the orphans accumulated.
    sciChart.renderableSeries.clear();
    for (const stale of this.markerDataSeries) {
      try {
        stale.delete();
      } catch {
        // already deleted
      }
    }
    this.markerDataSeries = [];

    const rows = this.temperatureDataRows.toArray();
    const baseUT = rows.length > 0 ? rows[0].UT : 0;

    // THE TEMP-OVER-TIME PLOT IS MADE OF MARKER SERIES AND NOTHING ELSE.
    //
    // Each marker contributes one series tracking that fibre position over time,
    // so a project with no usable marker produces no series at all — and SciChart
    // then falls back to a default 0:00:00-0:00:10 axis with nothing on it. That
    // reads as a still-loading chart, and an operator waits on it.
    //
    // Measured on beta-dev across 15 PAMAR projects: BKP Berlin is the only one
    // affected. Its sole marker is at P=0, which the guard below skips, so
    // "no marker" is logged twice and the legend comes back empty, while
    // S3632 REV_3 (marker at P=10) draws normally. That difference is the entire
    // blank-chart defect.
    //
    // NOT CHANGING WHAT P=0 MEANS. A marker at the liner start and an unset
    // marker are plausibly different facts, and this codebase already has a
    // documented scar from collapsing zero into absent (see length-format.ts:
    // "ZERO IS A RECORDED LENGTH"). Deciding that needs the data model, not a
    // render path the day before a demo. What changes here is only that the pane
    // SAYS it has nothing to draw instead of looking broken.
    let usableMarkers = 0;
    for (let m = 0; m < positions.length; m++) {
      const pos = positions.length > 0 ? positions[m].P : 0;
      if (pos == 0) {
        console.log('no marker');
        continue;
      }
      usableMarkers++;

      const series = new XyDataSeries(wasmContext);
      this.markerDataSeries.push(series);
      // pos is METRES (the server does not convert MAR); the axis and the label
      // are both in the user's system, so convert here and only here.
      const isMetric = this.projectService.isMetricUnit;
      const unitSuffix = isMetric ? 'm' : 'ft';
      const posDisplay = metresToDisplay(pos, isMetric);
      series.dataSeriesName = `Marker at ${posDisplay.toFixed(1)}${unitSuffix}`;

      // THE MARKER'S OWN COLOUR, not one picked from its position in the array.
      // This trace and the vertical line for the same marker on /curing and
      // /temperature now resolve from the identical source — MarkerInfo.C via
      // markerColor() — so an operator can tell which trace belongs to which
      // marker. It previously read a separate 13-entry palette indexed by `m`
      // while every marker LINE read the 18-step gradient stored on the marker,
      // so the two could not agree; and because `m` is a position, adding or
      // removing a marker reshuffled every trace colour.
      const markerColour = markerStroke(markerColor(positions[m].C));
      const lineSeries = new FastLineRenderableSeries(wasmContext, {
        stroke: markerColour,
        strokeThickness: 2.4,
        dataSeries: series,
      });
      // COLOUR-MATCH THE TOOLTIP TO THE TRACE. With eleven markers the only thing
      // tying a reading to its line is colour — desktop fills each box with the
      // series' own colour (CreateSeries<...>(marker.Color, ...)) and that is how a
      // stack of eleven stays readable.
      lineSeries.rolloverModifierProps.tooltipColor = markerColour;
      lineSeries.rolloverModifierProps.tooltipTextColor = '#FFFFFF';
      sciChart.renderableSeries.add(lineSeries);

      // CRE/CRB are in axis units, so the numerator must be too — otherwise this
      // fraction picks a different physical point on the liner per unit system.
      const percAtMarker =
        posDisplay / (this.tempData.CRE - this.tempData.CRB);

      const dataPointIndexAtPerc =
        Math.round(percAtMarker * (endDataPoint - startDataPoint)) +
        startDataPoint;

      var xValues: number[] = new Array(rows.length);
      var yValues: number[] = new Array(rows.length);

      // Update Temp Graph — over the retained window only.
      // The marker tracks ONE fiber position over time, so each row is sampled at
      // an absolute sample index. A trimmed or decimated row does not hold that
      // index at that array position, so go through the SIX mapping.
      // Temperature on X, time on Y — see the axis swap above.
      // OMIT NO-READING SAMPLES RATHER THAN PLOTTING THEM.
      //
      // Measured on beta-dev, S3632 REV_3: 25 of 309 points in this series were
      // exactly -273.15 — the "no reading" sentinel — appended as if they were
      // temperatures. The x axis auto-ranges to its data, so it ran to absolute
      // zero and squeezed a real 55-66 C trace into the top few percent of the
      // chart. Rob opened this tab during the call; it reads as broken.
      //
      // Dropping the point leaves a gap where there was no reading, which is
      // true. Clamping would invent a reading, and valueAtSampleIndex's `?? 0`
      // fallback for an absent index would invent 0 C — both worse than a gap.
      let kept = 0;
      for (let t = 0; t < rows.length; t++) {
        const temperature = valueAtSampleIndex(
          rows[t].TR,
          rows[t].SIX,
          dataPointIndexAtPerc
        );
        if (isNoReading(temperature)) {
          continue;
        }
        xValues[kept] = temperature;
        yValues[kept] = this.getTimeDiffInSec(baseUT, rows[t].UT);
        kept++;
      }
      xValues.length = kept;
      yValues.length = kept;

      series.appendRange(xValues, yValues);
    }

    // Nothing to plot: say so rather than leaving a default axis on screen.
    this.noMarkerSeries = usableMarkers === 0;
  }

  /** Project one temperature row into a waterfall z-row. O(width). */
  private buildWaterfallRow(
    row: TemperatureDataRow,
    startDataPoint: number,
    endDataPoint: number
  ): number[] {
    // The waterfall grid is uniform, so the row has to land on it whatever shape
    // it arrived in — full array, decimated, or liner-trimmed.
    return rowOnLinerGrid(row.TR, row.SIX, startDataPoint, endDataPoint);
  }

  /** Keep the waterfall matrix inside its row budget. */
  private trimWaterfall(): void {
    const overflow = this.heatMaps.length - this.WATERFALL_MAX_ROWS;
    if (overflow > 0) {
      this.heatMaps.splice(0, overflow);
    }
  }

  /**
   * FULL waterfall rebuild — backfill / project change only.
   *
   * This used to be called on every RT poll (from both the temperature and zone
   * RT handlers), rebuilding a ticks x width matrix and constructing a new
   * UniformHeatmapDataSeries each time while setupZoneDataSeries() added another
   * renderable series. RT now goes through appendWaterfallRows() instead.
   */
  /**
   * Keep the waterfall in step with the shared temperature rows.
   *
   * Same shape as /temperature's heatMaps projection and for the same reason:
   * deriving inside the data subscription would race the store's write. The
   * change kind decides the work — a backfill replaces the window so the series
   * is rebuilt, an append only needs the new rows projected — and folding
   * forward from our OWN high-water mark makes a missed notification self-heal.
   */
  private onTemperatureRowsChanged(change: TelemetryChange): void {
    const rows = this.temperatureDataRows;

    if (change.kind === 'clear') {
      this.heatMaps = [];
      this.lastWaterfallIndex = -1;
      return;
    }
    // The projection needs the liner geometry from the temperature payload and
    // a built surface. Until both exist, leave the mark alone so the next
    // notification redoes the work rather than skipping those rows.
    if (!this.waterfallSurface || !this.waterfallWasm || !this.tempData?.LES) {
      return;
    }

    if (change.kind === 'historical') {
      this.rebuildWaterfallChart(this.waterfallSurface, this.waterfallWasm);
      this.lastWaterfallIndex = rows.nextIndex - 1;
      return;
    }

    // Appending onto a placeholder would leave the fabricated blank row as the
    // first row of the cure, and keep the yStep that was guessed without data.
    if (this.waterfallIsPlaceholder) {
      this.rebuildWaterfallChart(this.waterfallSurface, this.waterfallWasm);
      this.lastWaterfallIndex = rows.nextIndex - 1;
      return;
    }

    const from = Math.max(this.lastWaterfallIndex + 1, rows.firstIndex);
    const fresh: TemperatureDataRow[] = [];
    for (let i = from; i < rows.nextIndex; i++) {
      const row = rows.get(i);
      if (row) {
        fresh.push(row);
      }
    }
    if (fresh.length > 0) {
      this.appendWaterfallRows(fresh);
      this.lastWaterfallIndex = rows.nextIndex - 1;
    }
  }

  /**
   * Seconds per waterfall row, MEASURED from the rows being plotted.
   *
   * THE TRUNCATION BUG. This used to be `MI` — the stored measurement interval —
   * and MI is measurably wrong: 5s claimed against a 14.5s real median, with
   * gaps to 116.1s. A UniformHeatmapDataSeries multiplies its yStep by the row
   * count to get its time extent, so a 3x-short step produced a 3x-short axis:
   * 47.5 minutes of waterfall against a 145.7 minute cure. The line chart beside
   * it derives from real UT timestamps (retainedTimeSpanSec), so the two
   * disagreed about how long the same cure was — which is the discriminator that
   * identified this.
   *
   * MI remains the fallback for a dataset too short to measure, because a chart
   * has to render something. That is a different trade from the offline pill,
   * which can and does hold 'unknown' rather than assert a state it cannot
   * support.
   *
   * STOPGAP, DELIBERATELY. A UniformHeatmapDataSeries requires a CONSTANT step
   * by definition, and the real spacing is not constant — 8.2s to 116.1s is a
   * 14x spread. An observed median makes this approximately right where it was
   * grossly wrong, which is the correct trade with a demo tomorrow, but rows
   * will still sit at slightly wrong times within the span. THE REAL FIX IS A
   * NON-UNIFORM SERIES keyed on each row's own timestamp. Do not mistake this
   * for that.
   */
  private waterfallRowSeconds(): number {
    const times: number[] = [];
    this.temperatureDataRows.forEachRetained((row) => {
      if (typeof row?.UT === 'number' && row.UT > 0) times.push(row.UT);
    });
    const medianMs = medianGapMs(times);
    if (medianMs !== null && medianMs > 0) {
      return medianMs / 1000;
    }
    return this.measurementInterval > 0 ? this.measurementInterval : 1;
  }

  /**
   * Re-frame the waterfall strip to the liner on every backfill.
   *
   * THE COUNTERPART THAT WAS NEVER WRITTEN. The main surface calls zoomExtents()
   * on every data arrival, gated on zoomLatch. The strip had no equivalent: xAxis2
   * is `autoRange: EAutoRange.Once`, so it frames itself to the FIRST heatmap it is
   * ever handed and then stops, and the only other writers of its range are the
   * zoom-out button and the range slider. Nothing re-framed it for a new project.
   *
   * SYMPTOM, measured on :4200 by the unit-preference session: a 120 ft project
   * drew a 0-200 ft strip above a correct 0-120 ft main chart. It was stable after
   * 14 s, so not a mid-render frame; it survived a FULL PAGE RELOAD, so it was not
   * live component state; and no range key exists in either storage. What carries
   * across the reload is `lastSelectedProject`, which makes the restore load the
   * PREVIOUS project first — its heatmap is the first one xAxis2 ever sees, Once
   * fires on that, and the strip keeps the other liner's extent for the life of
   * the page.
   *
   * This is ALSO the reader zoomLatch2 never had. It was armed (xAxis2
   * visibleRangeChanged) and reset (here and on zoom-out) but never read by
   * anything, which made the reset added for the in-app switch a no-op. Gating
   * here is what makes a slider drag survive the next poll.
   *
   * EXPLICIT RANGE, NOT zoomExtents(). The strip must show the LINER, not the
   * data's bounding box, and the extent is exactly CRE - CRB by construction: the
   * heatmap is (LES - LSS) columns of xStep (CRE - CRB)/(LES - LSS). This is the
   * same range the zoom-out button restores.
   */
  private reframeWaterfallExtent(xAxis2: NumericAxis): void {
    if (this.zoomLatch2.isZoomed) {
      return;
    }
    this.programmaticRangeChange = true;
    try {
      xAxis2.visibleRange = new NumberRange(
        0,
        this.channelrange_end - this.channelrange_start
      );
    } finally {
      setTimeout(() => (this.programmaticRangeChange = false), 0);
    }
  }

  rebuildWaterfallChart(sciChart: SciChartSurface, wasmContext: TSciChart) {
    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    const xFactor =
      (this.tempData.CRE - this.tempData.CRB) / (endDataPoint - startDataPoint);

    this.heatMaps = [];
    this.temperatureDataRows.forEachRetained((row) => {
      this.heatMaps.push(
        this.buildWaterfallRow(row, startDataPoint, endDataPoint)
      );
    });

    // TRAILING ALL-SENTINEL ROWS ARE NOT DATA, AND THEY PAINT AS A BLUE STRIPE.
    //
    // A DTS that has stopped recording writes raw ushort 0, which decodes to
    // -273.15 C. The waterfall's colour map has its cold end at -10 C, so every
    // one of those clamps to offset 0 and the row comes out PURE BLUE — a solid
    // bar across the foot of the chart that reads as "the whole liner went to
    // absolute zero" rather than "the instrument stopped".
    //
    // The artefact is not new. It was unnoticeable while the 3D Graph page
    // printed as a 90 mm band across the top of an otherwise empty sheet; taking
    // that to 200 mm to fill the page is what turns it into a stripe, so it is
    // this change's to carry rather than someone else's to find.
    //
    // TRAILING ONLY, AND THAT RESTRICTION IS THE WHOLE SAFETY ARGUMENT. The
    // heatmap is a UNIFORM series: yStart 0, a constant yStep, and a row's time
    // is its INDEX times that step. Dropping rows off the END shortens the axis,
    // which is correct — there are no readings there. Dropping a row from the
    // MIDDLE would silently shift every row after it earlier in time, which is a
    // far worse bug than the one being fixed. So the scan stops at the first row
    // that has a reading.
    //
    // Rebuild only, not appendWaterfallRows: a sentinel row arriving on the live
    // path is a gap that real rows will follow, and trimming there would make it
    // a middle deletion the moment they did.
    while (
      this.heatMaps.length > 0 &&
      this.heatMaps[this.heatMaps.length - 1]!.every((v) => isNoReading(v))
    ) {
      this.heatMaps.pop();
    }
    this.trimWaterfall();

    // A PLACEHOLDER IS NOT A RENDER, AND MUST NOT LOOK LIKE ONE.
    //
    // SciChart needs a non-empty matrix, so with no rows yet we still hand it one
    // blank row — but that row is a stand-in, not data. It is also where the
    // "0:00:00 -> 0:00:10" axis came from: one row times a yStep that fell back
    // to MI, presented as a finished, empty chart. Recording it lets the next
    // notification rebuild from scratch instead of appending real rows onto a
    // fabricated one.
    this.waterfallIsPlaceholder = this.heatMaps.length === 0;
    if (this.waterfallIsPlaceholder) {
      this.heatMaps.push(new Array<number>(endDataPoint - startDataPoint).fill(0));
    }

    // Release the previous series before replacing it; a new one is only ever
    // constructed here, not per poll.
    this.disposeWaterfallSeries(sciChart);

    const rowSeconds = this.waterfallRowSeconds();
    this.heatmapDataSeries1 = this.createHeatmapSeries(
      wasmContext,
      xFactor,
      rowSeconds
    );

    this.zoneDataLoaded = true;

    // ATTACH IT HERE, because THIS is the function that detached it.
    //
    // disposeWaterfallSeries() above calls renderableSeries.clear(). Re-adding was
    // the caller's job, and only ONE of the three callers did it — the
    // temperatureData subscription. onTemperatureRowsChanged calls this on both its
    // historical and its placeholder branch and never re-attached, so any rebuild
    // driven by a store notification left the surface holding a fresh data series
    // that nothing was drawing.
    //
    // MEASURED, in-app nav to /graph vs a direct URL load of the same project:
    //
    //            renderableSeries  heatMapRows  retained  placeholder  Y axis
    //   blank                   0          309   309/309        false  0 - 10
    //   working                 1          309   309/309        false  0 - 18558
    //
    // Everything upstream was healthy; only the attach was missing. The 0-10 Y axis
    // is a CONSEQUENCE — autoRange Once had no series to frame — not a placeholder.
    // The order differs because changed$ is a ReplaySubject(1): on the in-app path
    // the replayed notification lands AFTER the subscription's attach and strips it,
    // while on a direct load the subscription's attach happens last.
    //
    // Pairing the two in one function is the fix rather than adding a third call
    // site: a detach that relies on its callers to remember the re-attach will lose
    // that race again the next time a caller is added.
    this.setupZoneDataSeries(sciChart, wasmContext, this.heatmapDataSeries1);

    const first = this.temperatureDataRows.get(this.temperatureDataRows.firstIndex);
    if (first) {
      this.temp_startUnixTime = first.UT;
    }
  }

  /**
   * Incremental waterfall update for the RT path: append the new rows, trim, and
   * hand the same matrix back to the existing series. O(newRows x width) of JS
   * work instead of O(ticks x width), and zero allocation of chart objects.
   */
  appendWaterfallRows(rows: readonly TemperatureDataRow[]) {
    if (rows.length === 0) return;
    const startDataPoint = this.tempData.LSS;
    const endDataPoint = this.tempData.LES;

    for (const row of rows) {
      this.heatMaps.push(
        this.buildWaterfallRow(row, startDataPoint, endDataPoint)
      );
    }
    this.trimWaterfall();

    if (
      this.heatmapDataSeries1 &&
      typeof this.heatmapDataSeries1.setZValues === 'function'
    ) {
      this.heatmapDataSeries1.setZValues(this.heatMaps);
    }
    this.zoneDataLoaded = true;
  }

  /**
   * Build the heatmap series.
   *
   * Its own method purely as a SEAM: UniformHeatmapDataSeries needs a real WASM
   * context (it reaches for SCRTFloatVector), so with the construction inline the
   * rebuild's control flow could not be exercised under Karma at all — and the
   * control flow is exactly where the detach/attach pairing lives.
   */
  private createHeatmapSeries(
    wasmContext: TSciChart,
    xStep: number,
    yStep: number
  ): UniformHeatmapDataSeries {
    return new UniformHeatmapDataSeries(wasmContext, {
      xStart: 0,
      xStep,
      yStart: 0,
      yStep,
      zValues: this.heatMaps,
    });
  }

  /**
   * One tooltip line per marker: THE TEMPERATURE AT THE HOVERED TIME.
   *
   * THREE DEFECTS IN ONE BOX, from Rob's screenshots. Web read
   * "Marker at 60.8m" over "13483.9" — a LENGTH over a raw number — while desktop
   * shows the trace's temperature to one decimal with a degree suffix.
   *
   * 1. WRONG QUANTITY. On this surface TEMPERATURE IS X and time is Y (the axes are
   *    swapped here; see the comment at the marker loop). The old template pushed
   *    `formattedYValue`, so it reported time.
   * 2. RAW SECONDS. 13483.9 is 3h44m43s unformatted. Web sets
   *    `labelProvider.formatLabel`, which styles AXIS TICKS, but never
   *    `formatCursorLabel`, which is what SciChart uses for tooltips — so the
   *    tooltip fell back to the default numeric formatter. Desktop overrides both
   *    (ChartLabelProvider.cs:322-331 F2 for ticks, :305-314 F1 for the cursor).
   *    Fixed by formatting the value here rather than relying on either.
   * 3. THE NAME LINE. "Marker at 60.8m" is the series name
   *    (`dataSeriesName`, set in the marker loop). Desktop sets NO SeriesName on
   *    marker series at all (GraphViewModel.cs:1580), which is exactly why its
   *    tooltip has no line for a length to occupy. Dropped here for the same
   *    reason: colour ties the box to its trace, and the marker's position is
   *    still on the X-axis label. This line USED to say "the legend still carries
   *    the marker's position" — written when the legend was still here, and made
   *    false hours later by the very commit that removed it for the same
   *    desktop-parity reason. A comment that names a thing which no longer exists
   *    is worse than no comment.
   *
   * NO CONVERSION HERE, DELIBERATELY. Temperature rows arrive from the server
   * ALREADY in the caller's unit — which is why the heatmap colour map converts its
   * hardcoded -10/180 BOUNDS through unit_Temp_Val while leaving the data alone.
   * Converting again would read correct in one unit system and be silently wrong in
   * the other, which is the labelPostfix defect family inverted. The suffix and the
   * value therefore come from the same already-converted number.
   *
   * ONE DECIMAL matches desktop's F1 for the cursor. Its axis ticks use F2; match
   * one and you mismatch the other, so they are deliberately different.
   */
  private markerTooltipLines(seriesInfo: unknown): string[] {
    const info = seriesInfo as { xValue?: number } | null | undefined;
    const temperature = info?.xValue;
    if (typeof temperature !== 'number' || !Number.isFinite(temperature)) {
      return [];
    }
    const unit = this.projectService.isMetricUnit ? '\u00B0C' : '\u00B0F';
    return [`${temperature.toFixed(1)}${unit}`];
  }

  /**
   * The two TIME axes, retained for the shared tick. Cleared when the surfaces go.
   */
  private timeAxes: NumericAxis[] = [];

  /**
   * FOLLOW THE SLIDER. Registered with ChartTickService; see that file for why the
   * tick is shared rather than subscribed per screen.
   *
   * /graph had a tickPosition subscription and it was COMMENTED OUT, so dragging the
   * slider or pressing play moved /temperature and nothing else — Joel, on prod2:
   * "neither work outside the temperature tab".
   *
   * WRITTEN FRESH, NOT UNCOMMENTED. The dead block read
   * `this.temperatureDataRows.length` and `this.temperatureDataRows[0]`, but that is
   * a ReadonlyTelemetryBuffer now with neither a length nor index access, so it
   * could not compile, let alone run.
   *
   * WHAT IT DOES: widens the visible time window to the scrubbed position, which is
   * what the dead code intended and what the desktop does when Tick changes. Both
   * surfaces move together because both carry a time axis.
   *
   * NOT WHILE THE USER IS ZOOMED. The latch means the same thing here as everywhere
   * else: once they have taken the axes, the app stops moving them. And the write is
   * flagged programmatic so it does not arm the latch it just checked — the ordering
   * mistake the bench caught on return-to-live.
   */
  private onSharedTick(tick: number): void {
    if (this.zoomLatch.isZoomed || this.timeAxes.length === 0) {
      return;
    }
    const rows = this.temperatureDataRows;
    const first = rows.get(rows.firstIndex);
    const at = rows.get(Math.min(Math.max(tick, rows.firstIndex), rows.nextIndex - 1));
    if (!first || !at) {
      return;
    }
    const elapsed = this.getTimeDiffInSec(first.UT, at.UT);
    if (!(elapsed > 0)) {
      return;
    }
    this.programmaticRangeChange = true;
    try {
      for (const axis of this.timeAxes) {
        axis.visibleRange = new NumberRange(0, elapsed);
      }
    } finally {
      setTimeout(() => (this.programmaticRangeChange = false), 0);
    }
  }

  /** Release the waterfall renderable + data series. */
  private disposeWaterfallSeries(sciChart?: SciChartSurface): void {
    const surface = sciChart ?? this.waterfallSurface;
    if (surface) {
      try {
        surface.renderableSeries.clear();
      } catch {
        // surface already deleted
      }
    }
    if (
      this.heatmapDataSeries1 &&
      typeof (this.heatmapDataSeries1 as UniformHeatmapDataSeries).delete === 'function'
    ) {
      try {
        this.heatmapDataSeries1.delete();
      } catch {
        // already deleted
      }
    }
    this.heatmapDataSeries1 = {} as UniformHeatmapDataSeries;
  }

  /**
   * Desktop parity (GraphViewModel.UpdateAreaAnnotations): one Length×Time box
   * per fault on the waterfall — X from the fault's start position to the chart's
   * length max (a fault disturbs the measurement beyond its position), Y over the
   * derived detection→repair tick span. SciChart JS has no hatch brush, so we use
   * a solid low-opacity fill with a severity-coloured stroke instead of the
   * Desktop's 45° hatch VisualBrush.
   *
   * Mirror note: fault sample indices are mapped as on a non-mirrored trace;
   * MIR-specific index reversal is not applied (matches the Health▸Graph
   * annotations' existing convention).
   */
  updateWaterfallFaultAnnotations(sciChart: SciChartSurface) {
    // Remove only the boxes we drew last time (markers etc. stay).
    this.waterfallFaultAnnotations.forEach((ann) => {
      if (sciChart.annotations.contains(ann)) {
        sciChart.annotations.remove(ann);
      }
    });
    this.waterfallFaultAnnotations = [];

    if (this.faultSpans.length === 0 || this.lossData.LES == null) {
      return;
    }

    const lss = this.lossData.LSS;
    const les = this.lossData.LES;
    const linerLen = this.lossData.CRE - this.lossData.CRB;
    if (!(les > lss) || !(linerLen > 0)) {
      return;
    }
    const xFactor = linerLen / (les - lss);
    // Same time scale as the heatmap's uniform y axis (yStep = MI seconds/tick).
    // SAME SCALE AS THE HEATMAP, or the fault boxes land at the wrong times.
    const mi = this.waterfallRowSeconds();
    // Total ticks SEEN, not the retained window — span ticks are absolute, so
    // clamping to the window would truncate the waterfall's fault boxes.
    const lastTickTime = this.lossDataRows.totalCount * mi;

    this.faultSpans.forEach((span) => {
      const color = faultSeverityColor(span.FT);
      const xStart = Math.max(0, Math.min((span.PS - lss) * xFactor, linerLen));
      const y1 = span.startTick * mi;
      // Open spans run to "now"; closed spans end after their last observed tick.
      const y2 = Math.min((span.endTick + 1) * mi, lastTickTime);

      const box = new BoxAnnotation({
        fill: color,
        stroke: color,
        strokeThickness: 1,
        opacity: 0.3,
        x1: xStart,
        x2: linerLen,
        y1,
        y2,
      });
      sciChart.annotations.add(box);
      this.waterfallFaultAnnotations.push(box);
    });
  }

  formatDurationInSecToHHMMSS_Custom2(durationSec: number) {
    // Relative time is d.hh:mm:ss and never negative. Now that time is the
    // AUTO-RANGED axis, a degenerate range lays ticks slightly below zero, and
    // JS keeps the sign through % — the same path that produced "-1:-1:-1" on
    // the curing duration axis.
    if (!Number.isFinite(durationSec) || durationSec <= 0) {
      return '0:00:00';
    }
    const hours = Math.floor(durationSec / 3600);

    var days = Math.floor(hours / 24);
    var rem_hours = hours % 24;

    const minutes = '0' + Math.floor((durationSec % 3600) / 60);
    const seconds = '0' + Math.floor((durationSec % 3600) % 60);

    if (isNaN(hours)) {
      return '';
    }

    var formattedTime =
      rem_hours + ':' + minutes.substr(-2) + ':' + seconds.substr(-2);
    if (days > 0) {
      return days + '.' + formattedTime;
    } else {
      return formattedTime;
    }
  }

  getHours(unixTimestamp: number): number {
    var date;

    date = new Date(unixTimestamp);

    const hours = date.getUTCHours();
    if (isNaN(hours)) {
      return 0;
    }
    return hours;
  }

  getTimeDiffInSec(startUnix: number, endUnix: number): number {
    var date1, date2;

    date1 = new Date(startUnix);
    date2 = new Date(endUnix);

    var seconds = (date2.getTime() - date1.getTime()) / 1000;
    if (isNaN(seconds)) {
      return 0;
    }
    return seconds;
  }

  setupZoneDataSeries(
    sciChartSurface: SciChartSurface,
    wasmContext: TSciChart,
    heatMapDataSeries: UniformHeatmapDataSeries
  ) {
    const heatmapSeries = new UniformHeatmapRenderableSeries(wasmContext, {
      dataSeries: heatMapDataSeries,
      // Gradient and domain from chart-theme, shared with the legend beside this
      // chart and with the colour scale the report now draws on the 3D Graph
      // page. Three copies of the same ramp is how a report ends up mislabelling
      // the chart printed next to it. See GraphViewModel.cs ZoneGradient.
      colorMap: new HeatmapColorMap({
        minimum: this.projectService.unit_Temp_Val(WATERFALL_TEMP_DOMAIN_C.min),
        maximum: this.projectService.unit_Temp_Val(WATERFALL_TEMP_DOMAIN_C.max),
        gradientStops: WATERFALL_TEMP_GRADIENT_STOPS.map((s) => ({ ...s })),
      }),
    });

    const customTemplate = (heatmapSeries3d: any, tooltipTitle: any) => {
      const valuesWithLabels = [];
      if (tooltipTitle) {
        valuesWithLabels.push(tooltipTitle);
      }
      const heatmapSeriesInfo = heatmapSeries3d;
      valuesWithLabels.push(
        `${this.formatDurationInSecToHHMMSS_Custom2(
          heatmapSeriesInfo.formattedYValue
        )}`
      );
      valuesWithLabels.push(
        `${heatmapSeriesInfo.formattedZValue}` +
          (this.projectService.isMetricUnit
            ? String.fromCharCode(176).concat('C')
            : String.fromCharCode(176).concat('F'))
      );
      return valuesWithLabels;
    };
    this.rolloverModifier = new RolloverModifier({
      tooltipDataTemplate: customTemplate,
    });
    sciChartSurface.chartModifiers.add(this.rolloverModifier);
    heatmapSeries.rolloverModifierProps.tooltipTextColor = 'Black';

    // Create new modifier instances to avoid "already attached" errors
    this.rubberBandZoomModifier = new RubberBandXyZoomModifier();
    this.rubberBandZoomModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.rubberBandZoomModifier);

    this.zoomPanModifier = new ZoomPanModifier();
    this.zoomPanModifier.isEnabled = false;
    sciChartSurface.chartModifiers.add(this.zoomPanModifier);

    sciChartSurface.renderableSeries.add(heatmapSeries);

    this.right_onRangeSliderChange = (top, bot) => {
      //console.log('Series legend right:' + top + '-' + bot);
      const tempMax = this.projectService.unit_Temp_Val(180);
      //const tempMin = this.projectService.unit_Temp_Val(170);
      const tempMin = this.projectService.unit_Temp_Val(-10);

      //console.log('Temp Min-Max:' + tempMin + ' to ' + tempMax);
      const visibleMax =
        tempMax - Math.round((top / 100) * (tempMax - tempMin));
      const visibleMin =
        tempMin + Math.round(((100 - bot) / 100) * (tempMax - tempMin));
      //console.log('Series Min-Max:' + visibleMin + ' to ' + visibleMax);

      // RESOLVE THE TARGET AT CALL TIME. NEVER WRITE THROUGH THE CAPTURED ONE.
      //
      // MEASURED ON hybrid-dev, S3640 REV_1, /subviews, by driving the live app:
      // first page load the TEMP zoom works; navigate away to another tab and back
      // and it is dead. In the dead state this closure RUNS TO COMPLETION — 29
      // calls, correct arguments ([21, 84]), both slider values stored on the
      // component, both frame coalescers flushed (frame === null), zero rebuilds
      // during the drags — and the DRAWN series' colorMap stays at its full domain.
      // The body has no early return and nothing conditional, so the only thing
      // left is that `heatmapSeries` is not the renderable being drawn.
      //
      // A REMOUNT IS THE TRIGGER, and teardown is why. ngOnDestroy runs inside
      // runTeardown (dc948fd), which CATCHES AND LOGS rather than propagating, so a
      // throw partway through leaves the rest of the teardown undone and the route
      // remounts anyway. Everything after the throw site is skipped — the remaining
      // closeSubscription calls, `heatMaps = []`, and `waterfallSurface = undefined`
      // — so a half-released component can still hold and write a series nobody
      // draws. That is a correct fix (a teardown failure must not strand a route)
      // interacting with a captured reference here, which is the part that is ours.
      //
      // The legend's twin never broke because it closes over a `yAxis` created ONCE
      // in createTempLegend. Everything else in this chain already dereferences
      // live: the DOM binding calls the method on the instance, and the coalescer's
      // callback reads `this.right_onRangeSliderChange` at flush time. This was the
      // one captured reference left, so it is the one that could go stale.
      //
      // Falls back to the captured series when the surface cannot answer, so a
      // first drag before anything is attached behaves exactly as it did before.
      const drawn = this.waterfallSurface?.renderableSeries;
      const target =
        drawn && drawn.size() > 0
          ? (drawn.get(0) as UniformHeatmapRenderableSeries)
          : heatmapSeries;

      if (target?.colorMap) {
        target.colorMap.maximum = visibleMax;
        target.colorMap.minimum = visibleMin;
      }
    };
  }

  async createTempLegend() {
    // ONE SOURCE OF TRUTH FOR THE LEGEND'S DOMAIN.
    //
    // Both the colour map and the AXIS THAT LABELS IT are built from these two
    // values. They used to be computed once, for the colour map only, and the axis
    // was left on autoRange — so the strip was painted over one domain and labelled
    // with whatever the axis happened to settle on.
    //
    // MEASURED ON beta-dev 2026-09-18: the legend read 0-10 °F on
    // S3640 REV_1 while real cure temperatures run ~80-200 °F. 0-10 is SciChart's
    // DEFAULT NumericAxis visibleRange — what you get when autoRange has nothing to
    // range over. The number was never a temperature at all; it was the machinery's
    // default showing through a °F postfix, which is why it did not look like a
    // scaling error.
    //
    // NOT AN ENGINE BUG. The same unbound axis is in the prod2 bundle (SciChart
    // 3.1.348) as in beta-dev's (5.2.69) — read from both deployed artifacts, not
    // assumed. Relying on autoRange to reproduce a domain we already hold was the
    // defect; which engine happens to paper over it is a separate question.
    //
    // KEEP THESE TWO USES TOGETHER. If a future unit change touches the colour map
    // and not the axis, the legend silently lies — the strip says one thing and its
    // own scale says another, with nothing failing. That is exactly how this bug was
    // born, and legend-axis-binding.spec.ts exists to fail if they ever diverge.
    // BOTH fragments come from ONE call. Do not hand-roll either side — see
    // tempLegendRanges, and legend-axis-binding.spec.ts which pins them equal.
    const { colorMapRange, axisRange } = tempLegendRanges((c) =>
      this.projectService.unit_Temp_Val(c)
    );

    var options = {
      yAxisOptions: {
        axisBorder: {
          borderLeft: 1,
          color: '#FFFFFF77',
        },
        majorTickLineStyle: {
          color: 'White',
          tickSize: 6,
          strokeThickness: 1,
        },
        minorTickLineStyle: {
          color: 'White',
          tickSize: 3,
          strokeThickness: 1,
        },
        isVisible: false,
      },
      colorMap: {
        minimum: colorMapRange.minimum,
        maximum: colorMapRange.maximum,
        gradientStops: WATERFALL_TEMP_GRADIENT_STOPS.map((s) => ({ ...s })),
      },
    };

    const { heatmapLegend, wasmContext } = await HeatmapLegend.create(
      'temp-legend',
      options
    );

    const yAxis = new NumericAxis(wasmContext, {
      // BOUND, NOT AUTORANGED. The domain is known before the axis exists, so there
      // is nothing for autoRange to discover and no reason to let it try. Never +
      // an explicit visibleRange is correct under SciChart 3 and 5 alike, because it
      // does not depend on what either engine puts on the legend surface.
      autoRange: EAutoRange.Never,
      visibleRange: new NumberRange(axisRange.min, axisRange.max),
      labelFormat: ENumericFormat.Decimal,
      labelPrecision: 0,
      labelPostfix: this.projectService.isMetricUnit
        ? String.fromCharCode(176).concat('C')
        : String.fromCharCode(176).concat('F'),
      flippedCoordinates: false,
    });

    const legendSciChartSurface =
      heatmapLegend.innerSciChartSurface.sciChartSurface;

    legendSciChartSurface.yAxes.add(yAxis);

    this.right2_onRangeSliderChange = (top, bot) => {
      // THE LEGEND MUST DESCRIBE THE COLOUR MAP, so it re-ranges with exactly the
      // arithmetic of right_onRangeSliderChange — the closure that moves
      // colorMap.minimum/maximum. Any other formula here labels one thing and
      // paints another.
      //
      // WHAT THIS REPLACES, and why it produced a WRONG PICTURE rather than a
      // wrong-looking one. This used `tempMin = unit_Temp_Val(170)` and scaled by
      // `tempMax` instead of by the range. Those are CELSIUS landmarks — 170 - 180
      // happens to equal -10 — so the two formulas agree ONLY at (top=0, bot=100)
      // and ONLY in metric. In IMPERIAL AT REST it labelled the legend -18..356 F
      // against colours running 14..356 F: a 32 F error, exactly the C->F offset,
      // applied by toggleRangeSlider() the moment the slider panel is opened.
      //
      // Dragging the slider made the two diverge without bound. MEASURED: at
      // top=0, bot=78 the colour map's minimum has moved to 89 F, so a genuinely
      // warm 93.11 F cell sits at offset 0.0154 and paints PURE BLUE, while this
      // formula puts the legend's bottom label at 60 F. That is the defect Rob
      // reported on prod2 — one slider position reproduces BOTH numbers he read
      // out, which is what identified it.
      //
      // This changes NO colour. It makes the labels tell the truth about them.
      const tempMax = this.projectService.unit_Temp_Val(180);
      const tempMin = this.projectService.unit_Temp_Val(-10);
      const visibleMax =
        tempMax - Math.round((top / 100) * (tempMax - tempMin));
      const visibleMin =
        tempMin + Math.round(((100 - bot) / 100) * (tempMax - tempMin));

      yAxis.visibleRange = new NumberRange(visibleMin, visibleMax);
    };
  }

  setRollOver(val: boolean) {
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = val;
  }
  setPan(val: boolean) {
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = false;
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = val;
  }
  setZoom(val: boolean) {
    if (this.rolloverModifier) this.rolloverModifier.isEnabled = false;
    if (this.zoomPanModifier) this.zoomPanModifier.isEnabled = false;
    if (this.rubberBandZoomModifier) this.rubberBandZoomModifier.isEnabled = val;
  }

  zoomOut() {
    if (!this.onZoomOut) return;
    this.onZoomOut();
  }

  addMarker() {
    if (!this.onAddMarker) return;
    this.onAddMarker();
  }

  /** Delegates to the ONE marker colour resolver; see services/marker-color.ts. */
  convertRgbStringToColor(rgbString: string): string {
    return markerColor(rgbString);
  }

  updateVerticalAnnotations(sciChart: SciChartSurface, markers: MarkerInfo[]) {
    console.log('GraphComponent > updateVerticalAnnotations called with markers:', markers);
    
    // Clear all marker annotations
    sciChart.annotations.clear();
    
    if (!markers || markers.length === 0) {
      console.log('GraphComponent > No markers to add');
      // annotations.clear() also removed the fault overlay — redraw it.
      this.waterfallFaultAnnotations = [];
      this.updateWaterfallFaultAnnotations(sciChart);
      return;
    }

    markers.forEach((mar, index) => {
      // Convert RGB string "r, g, b" to format SciChart understands
      const colorString = this.convertRgbStringToColor(mar.C);
      
      const annt = new VerticalLineAnnotation({
        labelPlacement: ELabelPlacement.Axis,
        showLabel: true,
        stroke: colorString,
        strokeThickness: 3.1,
        x1: metresToDisplay(mar.P, this.projectService.isMetricUnit),
        axisLabelFill: colorString,
        axisFontSize: 14,
        isEditable: true,
        isSelected: false,
        onDragStarted: () => {
          // Increase thickness during drag for better visibility
          annt.strokeThickness = 6.5;
        },
        onDragEnded: () => {
          // annt.x1 is in AXIS units; P is persisted in metres.
          const metres = +displayToMetres(
            annt.x1,
            this.projectService.isMetricUnit
          ).toFixed(2);
          // Update the position in the internal array
          mar.P = metres;
          // Persist to API
          this.projectService.updateVerticalAnnotationPosition(index, metres);
          // Reset to normal thickness after drag
          annt.strokeThickness = 3.1;
        },
      });

      sciChart.annotations.add(annt);
    });

    // annotations.clear() above also removed the fault overlay — redraw it.
    this.waterfallFaultAnnotations = [];
    this.updateWaterfallFaultAnnotations(sciChart);
  }

  updateChartVisibleRange(leftVal: string, rightVal: string) {
    if (!this.onRangeSliderChange) return;

    if (+leftVal <= +rightVal) {
      this.rangeSliderLeftVal = +leftVal;
      this.rangeSliderRightVal = +rightVal;
    } else {
      this.rangeSliderLeftVal = +rightVal;
      this.rangeSliderRightVal = +leftVal;
    }

    this.bottomRangeFrames.submit([this.rangeSliderLeftVal, this.rangeSliderRightVal]);
  }

  left_updateChartVisibleRange(
    botVal: string,
    topVal: string,
    isTopThumb: boolean
  ) {
    var iTopVal = +topVal;
    var iBotVal = +botVal;

    //console.log(iTopVal + '---' + iBotVal);

    if (isTopThumb) {
      //console.log('isTopThumb');
      iTopVal = 100 - iTopVal;
      iBotVal = 100 - +botVal;
    } else {
      iTopVal = 100 - +topVal;
      iBotVal = 100 - iBotVal;
    }

    //console.log(iTopVal + '===' + iBotVal);

    //console.log(iLeftVal + '---' + iRightVal);
    if (!this.left_onRangeSliderChange) return;
    if (+iTopVal <= +iBotVal) {
      this.left_rangeSliderTopVal = iTopVal;
      this.left_rangeSliderBotVal = iBotVal;
    } else {
      this.left_rangeSliderTopVal = iBotVal;
      this.left_rangeSliderBotVal = iTopVal;
    }
    this.leftRangeFrames.submit([this.left_rangeSliderTopVal, this.left_rangeSliderBotVal]);
  }

  right_updateChartVisibleRange(
    botVal: string,
    topVal: string,
    isTopThumb: boolean
  ) {
    var iTopVal = +topVal;
    var iBotVal = +botVal;
    //console.log(iTopVal + '---' + iBotVal);

    if (isTopThumb) {
      console.log('isTopThumb');
      iTopVal = 100 - iTopVal;
      iBotVal = 100 - +botVal;
    } else {
      iTopVal = 100 - +topVal;
      iBotVal = 100 - iBotVal;
    }

    //console.log(iTopVal + '===' + iBotVal);

    //console.log(iLeftVal + '---' + iRightVal);
    if (!this.right_onRangeSliderChange) return;
    if (+iTopVal <= +iBotVal) {
      this.right_rangeSliderLeftVal = iTopVal;
      this.right_rangeSliderRightVal = iBotVal;
    } else {
      this.right_rangeSliderLeftVal = iBotVal;
      this.right_rangeSliderRightVal = iTopVal;
    }
    this.rightRangeFrames.submit([this.right_rangeSliderLeftVal, this.right_rangeSliderRightVal]);

    if (!this.right2_onRangeSliderChange) return;
    this.right2RangeFrames.submit([this.right_rangeSliderLeftVal, this.right_rangeSliderRightVal]);
  }

  toggleRangeSlider() {
    this.showRangeSlider = !this.showRangeSlider;

    if (!this.onRangeSliderChange) return;
    this.onRangeSliderChange(0, 100);

    if (!this.left_onRangeSliderChange) return;
    this.left_onRangeSliderChange(0, 100);

    if (!this.right_onRangeSliderChange) return;
    this.right_onRangeSliderChange(0, 100);

    if (!this.right2_onRangeSliderChange) return;
    this.right2_onRangeSliderChange(0, 100);
  }

  toggleHelpOverlay() {
    this.projectService.isHelpOverlayVisible =
      !this.projectService.isHelpOverlayVisible;
  }
}
